import React from 'react';

import {
	type CURRENCY,
	type EVENT_CATEGORY,
	type EventExtraData,
	type HexAlpha,
	type REGION,
	type UUID,
	type ticketTypeGroupExtraDataWithDefaultsSchema,
} from '@nfctron/nexus-core';

import { type z } from 'zod';

import { type FindOneEventResultDto } from '@root/features/api-client/generated/pass.api';

import { type EventKindData } from '@root/lib/constants/eventKind';
import { type EventTypeData } from '@root/lib/constants/eventType';
import { type PaymentMethodEnumTicketsData } from '@root/lib/constants/paymentMethod';
import { type CompanyEntity } from '@root/lib/entities/company.entity';
import { type EventColorConfig, type EventCurrency } from '@root/lib/entities/event.entity';
import { type OrganizerEntity } from '@root/lib/entities/organizer.entity';

/**
 * Event/Cart/Page context
 * @type {React.Context<EventCartPageContextProps | undefined>}
 * @export
 */
export const EventCartPageContext = React.createContext<EventCartPageContextProps | undefined>(undefined);

/**
 * Event/Cart/Page context props type
 * @export
 */
export type EventCartPageContextProps = {
	event: CartContextEventData;
	footerRef: React.RefObject<HTMLDivElement | null>;
	containerColors: { backgroundColor: HexAlpha; borderColor: HexAlpha };
	state: {
		isCartEnabled: boolean;
		isTicketSaleEnabled: boolean;
		isTicketImportEnabled: boolean;
		isPhysicalTicketTopUpEnabled: boolean;
	};
};

export type CartContextEventData = {
	eventId: UUID;
	slug: string;
	namePub: string;
	subtitle?: string;
	description: string;
	descriptionMd?: string;
	place: string;
	headerImageUrl?: string;
	takesPlaceFrom: Date;
	takesPlaceTo: Date;
	openInfo?: string;
	logoUrl?: string;
	receiptLogoUrl?: string;
	eventColors: EventColorConfig;
	currency: EventCurrency;
	extraCurrencies: Array<{
		currencyId: CURRENCY;
		rate: number;
	}>;
	eventType: EventTypeData;
	eventKind: EventKindData;
	holderRequired: boolean;
	saleInfo: {
		ticketsSet: boolean;
		saleFrom?: Date | null;
		saleTo?: Date | null;
	};
	extraData: EventExtraData;
	paymentMethods: PaymentMethodEnumTicketsData[];
	bankAccounts: Array<{ bankAccountId: UUID; currencyId: CURRENCY }>;
	locationVenue?: {
		venueId: UUID;
		name: string;
		longitude: number;
		latitude: number;
		description?: string;
	};
	reservationVenue?: { name: string };
	categories: Array<{ categoryId: EVENT_CATEGORY }>;
	regionId: REGION;
	organizer: OrganizerEntity;
	ticketCompany: CompanyEntity;
	productCompany: CompanyEntity;
} & ExtrasThatHaveNotBeenMovedToExtraDataYet;

export type UseInitCartPageData = FindOneEventResultDto & ExtrasThatHaveNotBeenMovedToExtraDataYet;
type ExtrasThatHaveNotBeenMovedToExtraDataYet = {
	queueLimit?: number;
	/** FIXME: add to extraData instead */
	generateEmptyChips?: boolean;
	defaultTicketTypeGroup?: {
		ticketTypeGroupId: UUID;
		namePub: string;
		displayOrder: number;
		extraData: z.infer<typeof ticketTypeGroupExtraDataWithDefaultsSchema>;
	};
};
